/**
 * Comprehensive Icon Mapping Utility
 * Maps IDs and labels from API responses to icon names/components
 * Handles variations, synonyms, and fuzzy matching
 */

import React from 'react';
import {
  FaBus,
  FaUtensils,
  FaWifi,
  FaUserTie,
  FaBroom,
  FaWheelchair,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import {
  Coins,
  CloudSun,
  Camera,
  ShoppingBag,
  Store,
  Utensils,
  Waves,
  Dumbbell,
  Wifi,
  Activity,
  Navigation,
  Zap,
  CreditCard,
  TreePine,
  Shield,
  ShieldCheck,
  Lock,
  CheckCircle2,
  Heart,
  Baby,
  Users,
  UserCheck,
  Handshake,
  Home,
  UserCircle,
  Sparkles,
  AlertCircle,
  Lightbulb,
  Footprints,
  Star,
  ParkingCircle,
  Wind,
  Clock,
  Stethoscope,
  Tv,
  Wine,
  Cigarette,
  PawPrint,
  Shirt,
  Coffee,
  ChefHat,
  WashingMachine,
  ArrowUpDown,
  Plug,
} from 'lucide-react';

/**
 * Travel Mode ID to Icon Name Mapping
 * Maps API travel mode IDs to icon names used in getTravelIcon
 */
export const TRAVEL_MODE_ICON_MAP: Record<string, string> = {
  air: 'plane',
  flight: 'plane',
  plane: 'plane',
  airplane: 'plane',
  train: 'train',
  railway: 'train',
  metro: 'train',
  subway: 'train',
  'rapid transit': 'train',
  bus: 'bus',
  car: 'car',
  drive: 'car',
  driving: 'car',
  'private transfer': 'car',
  'rental car': 'car',
  car_rental: 'car',
  'car rental': 'car',
  taxi: 'car',
  'taxi / colectivo': 'car',
  taxi_colectivo: 'car',
  colectivo: 'car',
  rickshaw: 'car',
  auto: 'car',
  'auto rickshaw': 'car',
  boat: 'ship',
  ship: 'ship',
  cruise: 'ship',
  ferry: 'ship',
  cycling: 'bicycle',
  bike: 'bicycle',
  bicycle: 'bicycle',
  walking: 'trekking',
  walk: 'trekking',
  trekking: 'trekking',
  hike: 'trekking',
};

/**
 * Amenity ID to Icon Name Mapping
 * Handles both underscore and hyphen variations
 */
export const AMENITY_ID_ICON_MAP: Record<string, string> = {
  // Cleanliness
  cleanliness: 'clean-hands',
  'clean-hands': 'clean-hands',
  clean: 'clean-hands',
  hygiene: 'clean-hands',

  // Water
  water: 'water',
  'drinking-water': 'water',
  drinking_water: 'water',
  'drinking water': 'water',

  // Toilet
  toilet: 'toilet',
  'public-toilets': 'toilet',
  toilet_facility: 'toilet',
  'toilet facility': 'toilet',
  restroom: 'toilet',
  bathroom: 'toilet',

  // Medical
  medical: 'medical',
  'medical-facilities': 'medical',
  medical_facilities: 'medical',
  'medical facilities': 'medical',
  hospital: 'medical',
  clinic: 'medical',
  chemist_medshop: 'medical',
  'chemist / medshop': 'medical',
  chemist: 'medical',
  pharmacy: 'medical',

  // Food
  restaurants: 'restaurants',
  restaurant: 'restaurants',
  food: 'restaurants',
  snack_food: 'restaurants',
  'snack / food availability': 'restaurants',
  'snack food': 'restaurants',
  dining: 'restaurants',

  // Accommodation
  accommodation: 'accommodation',
  hotel: 'accommodation',
  stay: 'accommodation',
  lodging: 'accommodation',

  // Transportation
  transportation: 'transportation',
  transport: 'transportation',
  transit: 'transportation',

  // Gym
  gym: 'gym',
  fitness: 'gym',
  workout: 'gym',

  // Swimming
  swimming: 'swimming',
  pool: 'swimming',
  'swimming pool': 'swimming',

  // WiFi
  wifi: 'wifi',
  'wi-fi': 'wifi',
  'wi-fi nearby': 'wifi',
  'wifi nearby': 'wifi',
  internet: 'wifi',
  connectivity: 'wifi',

  // Timings
  timings: 'clock',
  'timings at location': 'clock',
  timing: 'clock',
  hours: 'clock',
  'opening hours': 'clock',

  // Parking
  parking: 'parking',
  'car-parking': 'parking',
  'valet-parking': 'parking',
  'parking-lot': 'parking',

  // AC
  ac: 'ac',
  'air-conditioning': 'ac',
  'air conditioning': 'ac',
  cooling: 'ac',

  // Laundry
  laundry: 'laundry',
  'washing-machine': 'laundry',
  'dry-cleaning': 'laundry',

  // TV
  tv: 'tv',
  television: 'tv',

  // Bar
  bar: 'bar',
  drinks: 'bar',
  lounge: 'bar',

  // Pets
  'pet-friendly': 'pets',
  pets: 'pets',
  dogs: 'pets',

  // Breakfast
  breakfast: 'breakfast',
  'free-breakfast': 'breakfast',
};

/**
 * Activity Label to Icon Mapping
 * Handles common activity labels with fuzzy matching
 */
export const ACTIVITY_LABEL_ICON_MAP: Record<
  string,
  | IconType
  | React.ComponentType<{ size?: number; className?: string; color?: string }>
  | string // Allow SVG paths as strings
> = {
  // Nature & Wildlife - using SVG assets from icons folder
  'good for nature views': '/assets/images/icons/nature.svg',
  'nature views': '/assets/images/icons/nature.svg',
  nature: '/assets/images/icons/nature.svg',
  wildlife: '/assets/images/icons/wildlife.svg',
  'wild life': '/assets/images/icons/wildlife.svg',
  'dense forest': TreePine,
  forest: '/assets/images/icons/nature.svg',

  // Mountains & Hills - using SVG assets from icons folder
  mountains: '/assets/images/icons/mountains.svg',
  'mountains/hills': '/assets/images/icons/mountains.svg',
  'mountains / hills': '/assets/images/icons/mountains.svg',
  hills: '/assets/images/icons/mountains.svg',
  mountain: '/assets/images/icons/mountains.svg',
  'mountain views': '/assets/images/icons/mountains.svg',
  peaks: '/assets/images/icons/mountains.svg',

  // Climbing & Trekking - using SVG assets from icons folder
  climbing: '/assets/images/icons/trekking.svg',
  'good for climbing': '/assets/images/icons/trekking.svg',
  'good for trekking': '/assets/images/icons/trekking.svg',
  trekking: '/assets/images/icons/trekking.svg',
  hiking: '/assets/images/icons/trekking.svg',
  trek: '/assets/images/icons/trekking.svg',
  hike: '/assets/images/icons/trekking.svg',

  // Activities - using Lucide icons
  'paid activities': Coins, // Paid activities use Coins icon to indicate payment
  activities: Zap,
  'activities nearby': Activity, // Activities nearby use Activity icon
  'nearby activities': Activity,
  'nearby attractions': Star, // Nearby attractions use Star icon for points of interest
  attractions: Star,
  'attractions nearby': Star,
  'guided tour': '/assets/images/icons/guided.svg',
  'guided tours': '/assets/images/icons/guided.svg',
  guided: '/assets/images/icons/guided.svg',
  camping: '/assets/images/icons/campfire.svg',
  camp: '/assets/images/icons/campfire.svg',
  motorcycle: '/assets/images/icons/motorcycle.svg',
  'motorcycle ride': '/assets/images/icons/motorcycle.svg',
  'bike ride': '/assets/images/icons/motorcycle.svg',

  // Photography - using Lucide icons
  photography: Camera,
  photo: Camera,
  camera: Camera,
  'good for photography': Camera,

  // Shopping - using Lucide icons
  shopping: ShoppingBag,
  'shopping nearby': ShoppingBag,
  market: ShoppingBag,
  stores: Store,

  // Food & Dining - using Lucide icons
  food: Utensils,
  dining: Utensils,
  restaurant: Utensils,
  restaurants: Utensils,
  'food nearby': Utensils,

  // Water Activities - using Lucide icons
  swimming: Waves,
  swim: Waves,
  'water activities': Waves,
  beach: Waves,
  'toilet facility': '/assets/images/icons/toilet.svg',
  toilet: '/assets/images/icons/toilet.svg',
  'toilet facilities': '/assets/images/icons/toilet.svg',
  'public toilets': '/assets/images/icons/toilet.svg',

  // Charging
  'public charging stations': Plug,
  'charging stations': Plug,
  'charging station': Plug,
  charging: Plug,

  // Safety & security (distinct from generic activity)
  'crowd control': Users,
  'first aid': '/assets/images/icons/medical.svg',
  firstaid: '/assets/images/icons/medical.svg',

  // Adventure & Sports - using SVG assets from icons folder and Lucide icons
  adventure: Activity,
  'adventure activities': Activity,
  sports: '/assets/images/icons/sports.svg',
  outdoor: Activity,

  // Fitness - using Lucide icons
  fitness: Dumbbell,
  gym: Dumbbell,
  workout: Dumbbell,

  // Weather & Climate - using Lucide CloudSun icon
  weather: CloudSun,
  temperature: CloudSun,
  temp: CloudSun,
  sunrise: '/assets/images/icons/sunrise.svg',
  sunset: '/assets/images/icons/sunset.svg',

  // Money & Financial - using Lucide icons
  currency: Coins,
  'have money available': CreditCard, // Uses ATM icon (CreditCard)
  'money available': Coins,
  money: Coins,
  rupee: Coins,
  atm: CreditCard, // ATM uses CreditCard icon
  atms: CreditCard,

  // Water Bodies - using SVG assets from icons folder and Lucide icons
  'lakes, ponds': '/assets/images/icons/lakes.svg',
  lakes: '/assets/images/icons/lakes.svg',
  ponds: '/assets/images/icons/lakes.svg',
  river: Waves,
  rivers: Waves,

  // Emergency Contacts - using SVG assets from icons folder
  police: '/assets/images/icons/police.svg',
  cop: '/assets/images/icons/police.svg',
  'fire department': '/assets/images/icons/fire.svg',
  emergency: '/assets/images/svg/emergency/emergency.svg',
  contact: '/assets/images/svg/emergency/emergency.svg',
  'emergency contact': '/assets/images/svg/emergency/emergency.svg',

  'wi-fi nearby': Wifi,
  'wifi nearby': Wifi,
  'wi fi nearby': Wifi,
  wifi: Wifi,
  'medical facilities': Stethoscope,
  'medical facility': Stethoscope,
  medical: Stethoscope,
  hospital: Stethoscope,
  ambulance: Stethoscope,

  // Timings & Timezone - using Lucide Clock or SVG
  timings: Clock,
  'timings at location': Clock,
  timing: Clock,
  hours: Clock,
  'opening hours': Clock,
  timezone: '/assets/images/icons/timezone.svg',
  'time zone': '/assets/images/icons/timezone.svg',
  clock: Clock,
  time: Clock,

  // Additional Stats - using Lucide icons and SVG assets from icons folder
  population: Users,
  people: Users,
  humidity: '/assets/images/icons/humidity.svg',
  'air pressure': '/assets/images/icons/pressure.svg',
  pressure: '/assets/images/icons/pressure.svg',
  'air quality': CloudSun,
  quality: CloudSun,

  // Safety & Security - using Lucide icons
  'child friendly': Baby,
  'child safety': Baby,
  'women safe': UserCircle,
  'women travel safety': UserCircle,
  'lgbtq comforts': Sparkles,
  'disability friendly': FaWheelchair, // Keep Font Awesome as Lucide doesn't have wheelchair
  'group tours': Users,
  'clean locations': FaBroom, // Keep Font Awesome as Lucide doesn't have broom
  'no danger zones': CheckCircle2,
  'safe zones': Home,
  'low crime rates': Shield,
  'very safe': CheckCircle2,
  'security measures taken': Lock,
  'personal safety': ShieldCheck,
  'traveler advisories': UserCheck,
  'emergency evacuation': '/assets/images/svg/emergency/emergency.svg', // Emergency icon
  'local friendly': Handshake,
  'family safe': Heart,
  'safe zone': Home,
  'clean location': FaBroom, // Keep Font Awesome as Lucide doesn't have broom
  'supervised area': UserCircle,
  'emergency services': AlertCircle, // Emergency Services icon
  'staff onsite': UserCircle,
  'staff on-site': UserCircle,

  // Lighting & Area Characteristics - using Lucide icons
  'well lit': Lightbulb,
  'well-lit': Lightbulb,
  well_lit: Lightbulb,
  lighting: Lightbulb,
  'good lighting': Lightbulb,
  'busy area': Users,
  'busy-area': Users,
  busy_area: Users,
  crowded: Users,
  'pedestrian only': Footprints,
  'pedestrian-only': Footprints,
  pedestrian_only: Footprints,
  'pedestrian zone': Footprints,
  'walking zone': Footprints,
  'walking only': Footprints,

  // Hotel & Stay Amenities
  parking: ParkingCircle,
  'car parking': ParkingCircle,
  'valet parking': ParkingCircle,
  'air conditioning': Wind,
  ac: Wind,
  laundry: Shirt,
  'washing machine': WashingMachine,
  tv: Tv,
  television: Tv,
  bar: Wine,
  drinks: Wine,
  pets: PawPrint,
  'pet friendly': PawPrint,
  breakfast: Coffee,
  'free breakfast': Coffee,
  elevator: ArrowUpDown,
  lift: ArrowUpDown,
  kitchen: ChefHat,
  kitchenette: ChefHat,
  smoking: Cigarette,
  'smoking area': Cigarette,
};

/**
 * Normalize a string for matching (lowercase, trim, replace special chars)
 */
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/^by\s+/i, '') // Remove "by" prefix (case-insensitive)
    .replace(/[_\s-]+/g, ' ') // Replace underscores, hyphens, multiple spaces with single space
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
}

/**
 * Get icon name for travel mode ID
 */
export function getTravelModeIconName(
  id: string | null | undefined
): string | null {
  if (!id) return null;
  const normalized = normalizeString(id);
  return TRAVEL_MODE_ICON_MAP[normalized] || null;
}

/**
 * Get icon name for amenity ID
 */
export function getAmenityIconName(
  id: string | null | undefined
): string | null {
  if (!id) return null;
  const normalized = normalizeString(id);
  return AMENITY_ID_ICON_MAP[normalized] || null;
}

/**
 * Get icon component for activity label with fuzzy matching
 */
export function getActivityIconComponent(
  label: string | null | undefined
):
  | IconType
  | React.ComponentType<{ size?: number; className?: string; color?: string }>
  | string
  | null {
  if (!label) {
    console.log('❌ [getActivityIconComponent] No label provided');
    return null;
  }

  const normalized = normalizeString(label);
  console.log('🔍 [getActivityIconComponent] Looking for icon:', {
    label,
    normalized,
  });

  // Try exact match first
  if (ACTIVITY_LABEL_ICON_MAP[normalized]) {
    console.log('✅ [getActivityIconComponent] Found exact match:', normalized);
    return ACTIVITY_LABEL_ICON_MAP[normalized];
  }

  // Try partial matching - check if any key is contained in the label
  for (const [key, icon] of Object.entries(ACTIVITY_LABEL_ICON_MAP)) {
    // Normalize the key as well for better matching
    if (normalized.includes(normalizeString(key))) {
      console.log('✅ [getActivityIconComponent] Found partial match:', {
        normalized,
        key,
      });
      return icon;
    }
  }

  // Try keyword matching
  const keywordMappings: Array<{
    keywords: string[];
    icon:
      | IconType
      | React.ComponentType<{
          size?: number;
          className?: string;
          color?: string;
        }>
      | string; // Allow SVG paths as strings
  }> = [
    {
      keywords: ['nature', 'tree'],
      icon: '/assets/images/icons/nature.svg',
    },
    {
      keywords: ['dense forest'],
      icon: TreePine,
    },
    {
      keywords: ['forest'],
      icon: '/assets/images/icons/nature.svg',
    },
    {
      keywords: ['wildlife', 'wild life'],
      icon: '/assets/images/icons/wildlife.svg',
    },
    {
      keywords: ['mountain', 'hill', 'peak', 'climb'],
      icon: '/assets/images/icons/mountains.svg',
    },
    {
      keywords: ['trek', 'hike', 'trekking', 'hiking'],
      icon: '/assets/images/icons/trekking.svg',
    },
    { keywords: ['walk'], icon: Navigation },
    { keywords: ['swim', 'water', 'beach', 'pool'], icon: Waves },
    { keywords: ['photo', 'camera', 'picture'], icon: Camera },
    { keywords: ['shop', 'buy', 'market'], icon: ShoppingBag },
    { keywords: ['store', 'stores'], icon: Store },
    { keywords: ['food', 'eat', 'dine', 'restaurant'], icon: Utensils },
    { keywords: ['activity', 'activities'], icon: Zap },
    {
      keywords: [
        'charging',
        'charging stations',
        'charging station',
        'public charging',
      ],
      icon: Plug,
    },
    { keywords: ['crowd control', 'crowd'], icon: Users },
    {
      keywords: ['first aid', 'firstaid', 'first-aid'],
      icon: '/assets/images/icons/medical.svg',
    },
    { keywords: ['activities nearby', 'nearby activities'], icon: Activity },
    { keywords: ['attraction', 'attractions'], icon: Star },
    { keywords: ['nearby attractions', 'attractions nearby'], icon: Star },
    { keywords: ['adventure', 'outdoor'], icon: Activity },
    { keywords: ['sport', 'sports'], icon: '/assets/images/icons/sports.svg' },
    { keywords: ['fitness', 'gym', 'workout'], icon: Dumbbell },
    {
      keywords: ['weather', 'forecast'],
      icon: CloudSun,
    },
    {
      keywords: ['temperature', 'temp'],
      icon: CloudSun,
    },
    {
      keywords: ['sunrise', 'sun rise'],
      icon: '/assets/images/icons/sunrise.svg',
    },
    {
      keywords: ['sunset', 'sun set'],
      icon: '/assets/images/icons/sunset.svg',
    },
    {
      keywords: ['money', 'currency', 'available', 'atm', 'rupee'],
      icon: Coins,
    },
    {
      keywords: ['humidity', 'moisture'],
      icon: '/assets/images/icons/humidity.svg',
    },
    {
      keywords: ['pressure', 'air pressure', 'atmospheric'],
      icon: '/assets/images/icons/pressure.svg',
    },
    {
      keywords: ['timezone', 'time zone', 'clock', 'time'],
      icon: '/assets/images/icons/timezone.svg',
    },
    {
      keywords: ['population', 'people'],
      icon: Users,
    },
    {
      keywords: ['lake', 'pond', 'lakes'],
      icon: '/assets/images/icons/lakes.svg',
    },
    { keywords: ['water body', 'river'], icon: Waves },
    {
      keywords: ['police', 'cop'],
      icon: '/assets/images/icons/police.svg',
    },
    {
      keywords: ['ambulance', 'medical', 'hospital'],
      icon: '/assets/images/icons/ambulance.svg',
    },
    {
      keywords: ['fire', 'brigade', 'fire department'],
      icon: '/assets/images/icons/fire.svg',
    },
    { keywords: ['wifi', 'wi-fi', 'internet', 'connectivity'], icon: Wifi },
    { keywords: ['child', 'children'], icon: Baby },
    { keywords: ['family'], icon: Heart },
    { keywords: ['friendly'], icon: Handshake },
    { keywords: ['women', 'woman', 'female', 'safe'], icon: UserCircle },
    { keywords: ['lgbtq', 'lgbt', 'comfort'], icon: Sparkles },
    { keywords: ['disability', 'disabled', 'accessible'], icon: FaWheelchair },
    {
      keywords: ['safety', 'security', 'secure', 'shield', 'protection'],
      icon: Shield,
    },
    { keywords: ['safe'], icon: CheckCircle2 },
    { keywords: ['danger', 'zone', 'crime', 'advisory'], icon: Lock },
    {
      keywords: ['evacuation', 'emergency', 'emergency services'],
      icon: AlertCircle,
    },
    { keywords: ['local'], icon: Handshake },
    { keywords: ['group'], icon: Users },
    { keywords: ['guided', 'guide'], icon: '/assets/images/icons/guided.svg' },
    {
      keywords: ['camp', 'camping'],
      icon: '/assets/images/icons/campfire.svg',
    },
    {
      keywords: ['well lit', 'well-lit', 'lighting', 'lit', 'bright'],
      icon: Lightbulb,
    },
    {
      keywords: ['busy area', 'busy-area', 'crowded', 'busy', 'populated'],
      icon: Users,
    },
    {
      keywords: [
        'pedestrian only',
        'pedestrian-only',
        'pedestrian zone',
        'walking zone',
        'walking only',
        'pedestrian',
      ],
      icon: Footprints,
    },
    {
      keywords: ['motorcycle', 'bike ride'],
      icon: '/assets/images/icons/motorcycle.svg',
    },
    { keywords: ['tour'], icon: Users },
    { keywords: ['family safe'], icon: Heart },
    { keywords: ['safe zone'], icon: Home },
    { keywords: ['clean', 'clean location', 'cleanliness'], icon: FaBroom },
    { keywords: ['supervised', 'supervision'], icon: FaUserTie },
    { keywords: ['staff', 'onsite', 'on-site'], icon: FaUserTie },
    {
      keywords: ['timing', 'hours', 'opening', 'timezone', 'time zone'],
      icon: '/assets/images/icons/timezone.svg',
    },
  ];

  for (const { keywords, icon } of keywordMappings) {
    if (keywords.some(keyword => normalized.includes(keyword))) {
      console.log('✅ [getActivityIconComponent] Found keyword match:', {
        normalized,
        matchedKeywords: keywords.filter(k => normalized.includes(k)),
      });
      return icon;
    }
  }

  console.log('❌ [getActivityIconComponent] No match found for:', normalized);
  return null;
}

/**
 * Get React icon component for amenity (using react-icons)
 */
export function getAmenityIconComponent(
  id: string | null | undefined
): IconType | string | null {
  if (!id) return null;

  const normalized = normalizeString(id);

  // Map to react-icons components or SVG paths
  const amenityIconMap: Record<string, IconType | string> = {
    // Cleanliness - using SVG assets from icons folder
    'clean-hands': '/assets/images/icons/cleanliness.svg',
    cleanliness: '/assets/images/icons/cleanliness.svg',
    clean: '/assets/images/icons/cleanliness.svg',

    // Water - using SVG assets from icons folder
    water: '/assets/images/icons/water.svg',
    'drinking-water': '/assets/images/icons/water.svg',
    drinking_water: '/assets/images/icons/water.svg',
    'drinking water': '/assets/images/icons/water.svg',

    // Toilet - using SVG assets from icons folder
    toilet: '/assets/images/icons/toilet.svg',
    'public-toilets': '/assets/images/icons/toilet.svg',
    toilet_facility: '/assets/images/icons/toilet.svg',
    'toilet facility': '/assets/images/icons/toilet.svg',

    // Medical - using SVG assets from icons folder
    medical: '/assets/images/icons/medical.svg',
    'medical-facilities': '/assets/images/icons/medical.svg',
    medical_facilities: '/assets/images/icons/medical.svg',
    'medical facilities': '/assets/images/icons/medical.svg',
    hospital: '/assets/images/icons/medical.svg',
    clinic: '/assets/images/icons/medical.svg',
    // Chemist / MedShop - using SVG asset from svg folder
    chemist_medshop: '/assets/images/svg/pharmacy.svg',
    'chemist / medshop': '/assets/images/svg/pharmacy.svg',
    chemist: '/assets/images/svg/pharmacy.svg',
    pharmacy: '/assets/images/svg/pharmacy.svg',

    // Restaurants - keeping Lucide Utensils
    restaurants: FaUtensils,
    restaurant: FaUtensils,
    food: FaUtensils,
    snack_food: FaUtensils,
    'snack food': FaUtensils,

    // Accommodation - using SVG assets from icons folder
    accommodation: '/assets/images/icons/resorts.svg',
    hotel: '/assets/images/icons/resorts.svg',

    // Transportation - keeping FaBus
    transportation: FaBus,
    transport: FaBus,

    // Gym - using SVG assets from icons folder
    gym: '/assets/images/icons/gym.svg',
    fitness: '/assets/images/icons/gym.svg',

    // Swimming - using SVG assets from icons folder
    swimming: '/assets/images/icons/swimming.svg',
    pool: '/assets/images/icons/swimming.svg',

    // WiFi - keeping Lucide Wifi
    wifi: FaWifi,
    'wi-fi': FaWifi,
    internet: FaWifi,

    // Timings - using SVG assets from icons folder
    timings: '/assets/images/icons/timezone.svg',
    'timings at location': '/assets/images/icons/timezone.svg',
    timing: '/assets/images/icons/timezone.svg',
    hours: '/assets/images/icons/timezone.svg',
    'opening hours': '/assets/images/icons/timezone.svg',

    // New Amenities
    parking: ParkingCircle,
    'car-parking': ParkingCircle,
    ac: Wind,
    'air-conditioning': Wind,
    laundry: Shirt,
    tv: Tv,
    bar: Wine,
    pets: PawPrint,
    breakfast: Coffee,
    elevator: ArrowUpDown,
    kitchen: ChefHat,
    smoking: Cigarette,

    // Charging (distinct from Zap/lightning)
    charging: Plug,
    'charging-stations': Plug,
    'charging-station': Plug,
    'public-charging-stations': Plug,
    'public charging stations': Plug,
  };

  return amenityIconMap[normalized] || null;
}
